import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react';

import { Box } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';

import { AddAgreementAndPayments } from 'components/TablesDocs/AddAgreementAndPayments';
import { AgentReportsAndActs } from 'components/TablesDocs/AgentReportsAndActs';

import { CustomTab, CustomTabs } from 'ui/tabs/tabs';

import { useGetSupplementaryAgreements } from 'hooks/documents/useGetSupplementaryAgreements';
import { useGetReportFile } from 'hooks/documents/useGetReportFiles';

import { BorderLinearProgress } from 'components/Loader/Loader';
import { DocumentsFilters } from 'components/MainFilters/DocumentsFilters';
import { DocumentsNavigation } from 'components/Navigation/DocumentsNavigation';
import { Company, DataPeriod, ErrorData, IManagersHR } from 'interfaces/global/globalInterfaces';
import { sessionStorageGetItem } from 'utils/sessionStorageGetItem';
import { useGetCompanies } from 'hooks/companies/useGetCompanies';
import { useGetServicePeriods } from 'hooks/periods/useGetServicePeriods';

import { ReactComponent as DownloadIconComponent } from './download.svg';
import { DocumentViewModal } from 'shared/modals/DocumentViewModal/DocumentViewModal';
import { isAccessAllowed } from 'utils/isAccessAllowed';
import { InfoText } from 'ui/other/Typography';
import { useGetCompanyAdmins } from 'hooks/permissions/useGetCompanyAdmins';
import { useGetAccessList } from 'hooks/permissions/useGetAccessList';
import { useGetDefaultPermissions } from 'hooks/permissions/useGetDefaultPermissions';
import { useSnackbar } from 'notistack';
import { Button } from '@bestdoctor/spectrum-uikit/Button';

interface DocumentsProps {
  handleManagersHR: (value: IManagersHR | null) => void;
}

export const Documents: React.FC<DocumentsProps> = ({ handleManagersHR }) => {
  // sessionStorage для главных фильтров
  const mainFilterCompanyDocuments = sessionStorageGetItem<Company>('mainFilterCompanyDocuments');
  const mainFilterPeriodDocuments = sessionStorageGetItem<DataPeriod>('mainFilterPeriodDocuments');

  const [selectedPeriodByDocuments, setSelectedPeriodByDocuments] = useState<DataPeriod | null>(
    mainFilterPeriodDocuments || null
  );

  const [selectedCompanyByDocuments, setSelectedCompanyByDocuments] = useState<Company | null>(
    mainFilterCompanyDocuments || null
  );

  const [secondaryMenuValue, setSecondaryMenuValue] = useState<string>('docs1');

  const [orderingAgreement, setOrderingAgreement] = useState<string>('');

  const [onScrollOpen, setOnScrollOpen] = useState<boolean>(true);

  const [isDocumentViewModal, setDocumentViewModal] = useState<boolean>(false);

  const [modalLink, setModalLink] = useState<string | undefined>('');

  const token = localStorage.getItem('token');

  const { enqueueSnackbar } = useSnackbar();

  const { accessList } = useGetAccessList({
    isLoggedIn: !!token,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  const { defaultPermissions } = useGetDefaultPermissions({
    isLoggedIn: !!token,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  const { isLoadingCompanies, companiesSearch: allCompanies } = useGetCompanies();

  useEffect(() => {
    if (!allCompanies.length) {
      return;
    }

    const storedCompany = sessionStorageGetItem<Company>('mainFilterCompanyDocuments');
    if (!storedCompany?.uuid) {
      setSelectedCompanyByDocuments(allCompanies[0]);
      sessionStorage.setItem('mainFilterCompanyDocuments', JSON.stringify(allCompanies[0]));
      return;
    }

    const matchedCompany = allCompanies.find((company) => company.uuid === storedCompany.uuid) ?? null;
    if (matchedCompany) {
      setSelectedCompanyByDocuments(matchedCompany);
      sessionStorage.setItem('mainFilterCompanyDocuments', JSON.stringify(matchedCompany));
    }
  }, [allCompanies]);

  const {
    isLoadingSupplementaryAgreements,
    fetchNextPage,
    hasNextPageSupplementaryAgreements,
    isFetchingNextPageSupplementaryAgreements,
    dataSupplementaryAgreements,
    refetchSupplementaryAgreements,
  } = useGetSupplementaryAgreements({
    isSupplementaryAgreementsAllowed: isAccessAllowed(
      mainFilterCompanyDocuments?.uuid,
      'HR_docs',
      accessList,
      defaultPermissions
    ),
    company: selectedCompanyByDocuments,
    period: selectedPeriodByDocuments,
    ordering: orderingAgreement,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  const {
    isLoadingReportFiles,
    fetchNextPageActs,
    hasNextPageReportFiles,
    actsAndReports,
    isFetchingNextPageReportFiles,
  } = useGetReportFile({
    isReportFilesAllowed: isAccessAllowed(mainFilterCompanyDocuments?.uuid, 'HR_docs', accessList, defaultPermissions),
    company: selectedCompanyByDocuments,
    period: selectedPeriodByDocuments,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  const { isLoadingServicePeriods, dataServicePeriods } = useGetServicePeriods({
    companies: selectedCompanyByDocuments ? [selectedCompanyByDocuments] : [],
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response.json().then((result) => {
          result.errors.forEach((item: ErrorData) =>
            enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
          );
        });
      }
    },
  });

  const dataPeriods = useMemo(
    () => (dataServicePeriods ? [...dataServicePeriods].reverse() : []),
    [dataServicePeriods]
  );

  useEffect(() => {
    if (!selectedCompanyByDocuments) {
      setSelectedPeriodByDocuments(null);
      sessionStorage.setItem('mainFilterPeriodDocuments', JSON.stringify(''));
      return;
    }

    if (!dataServicePeriods) {
      return;
    }

    const storedPeriod = sessionStorageGetItem<DataPeriod>('mainFilterPeriodDocuments');
    const activePeriodExist = dataPeriods.some((item) => item.activePeriod);

    dataPeriods.forEach((period: DataPeriod) => {
      if (storedPeriod) {
        if (storedPeriod.uuid === period.uuid) {
          setSelectedPeriodByDocuments(period);
          sessionStorage.setItem('mainFilterPeriodDocuments', JSON.stringify(period));
          handleManagersHR({
            accountEmail: period.accountEmail,
            accountPhone: period.accountPhone,
            curatorEmail: period.curatorEmail,
            curatorPhone: period.curatorPhone,
            accountFullName: period.accountFullName,
            doctorFullName: period.doctorFullName,
            doctorEmail: period.doctorEmail,
          });
        }
      } else if (period.activePeriod) {
        setSelectedPeriodByDocuments(period);
        sessionStorage.setItem('mainFilterPeriodDocuments', JSON.stringify(period));

        handleManagersHR({
          accountEmail: period.accountEmail,
          accountPhone: period.accountPhone,
          curatorEmail: period.curatorEmail,
          curatorPhone: period.curatorPhone,
          accountFullName: period.accountFullName,
          doctorFullName: period.doctorFullName,
        });
      } else if (!activePeriodExist) {
        setSelectedPeriodByDocuments(dataPeriods[0]);
        sessionStorage.setItem('mainFilterPeriodDocuments', JSON.stringify(dataPeriods[0]));

        handleManagersHR({
          accountEmail: dataPeriods[0].accountEmail,
          accountPhone: dataPeriods[0].accountPhone,
          curatorEmail: dataPeriods[0].curatorEmail,
          curatorPhone: dataPeriods[0].curatorPhone,
          accountFullName: dataPeriods[0].accountFullName,
          doctorFullName: dataPeriods[0].doctorFullName,
        });
      }
    });
  }, [dataPeriods, dataServicePeriods, handleManagersHR, selectedCompanyByDocuments]);

  const { companyAdminsData } = useGetCompanyAdmins({
    companyUuid: selectedCompanyByDocuments?.uuid,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  const handleChange = (_event: SyntheticEvent, newValue: string): void => setSecondaryMenuValue(newValue);

  useEffect(() => {
    const handleScroll = (): void => {
      setOnScrollOpen(false);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    setModalLink(selectedPeriodByDocuments?.contract);
  }, [selectedPeriodByDocuments]);

  return (
    <>
      {isLoadingCompanies && isLoadingServicePeriods && isLoadingReportFiles && <BorderLinearProgress />}

      <DocumentsNavigation />

      <DocumentsFilters
        allCompanies={allCompanies}
        selectedCompanyByDocuments={selectedCompanyByDocuments}
        handleSelectedCompanyByDocuments={setSelectedCompanyByDocuments}
        dataPeriods={dataPeriods}
        selectedPeriodByDocuments={selectedPeriodByDocuments}
        handleSelectedPeriodByDocuments={setSelectedPeriodByDocuments}
      />

      {isAccessAllowed(mainFilterCompanyDocuments?.uuid, 'HR_docs', accessList, defaultPermissions) ? (
        <Box component="section" sx={{ marginTop: '32px' }}>
          <TabContext value={secondaryMenuValue}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <CustomTabs value={secondaryMenuValue} onChange={handleChange} aria-label="Навигация по документам">
                  <CustomTab value="docs1" label="Дополнительные соглашения и платежи" />
                  {selectedPeriodByDocuments?.contractType === 'fronting' &&
                    isAccessAllowed(
                      mainFilterCompanyDocuments?.uuid,
                      'HR_docs_reports',
                      accessList,
                      defaultPermissions
                    ) && <CustomTab value="docs2" label="Отчеты и прочие документы" />}
                </CustomTabs>
              </Box>
              <Box>
                <Button
                  variant="secondary"
                  leftElement={<DownloadIconComponent />}
                  onClick={() => setDocumentViewModal(true)}
                  disabled={!modalLink}
                >
                  Выгрузить договор
                </Button>
              </Box>
            </Box>

            <DocumentViewModal
              link={modalLink}
              title="Предпросмотр документа"
              open={isDocumentViewModal && !!modalLink}
              handleClose={() => setDocumentViewModal(false)}
            />

            {isLoadingSupplementaryAgreements && <BorderLinearProgress />}
            <TabPanel
              value="docs1"
              sx={{
                padding: '24px 0',
              }}
            >
              <AddAgreementAndPayments
                handleOnScroll={setOnScrollOpen}
                onScrollOpen={onScrollOpen}
                supplementaryAgreements={dataSupplementaryAgreements}
                fetchNextPage={fetchNextPage}
                isFetchingNextPageSupplementaryAgreements={isFetchingNextPageSupplementaryAgreements}
                hasNextPageSupplementaryAgreements={hasNextPageSupplementaryAgreements}
                handleOrderingAgreement={setOrderingAgreement}
                orderingAgreement={orderingAgreement}
                accessList={accessList}
                defaultPermissions={defaultPermissions}
                refetchSupplementaryAgreements={refetchSupplementaryAgreements}
              />
            </TabPanel>

            {selectedPeriodByDocuments?.contractType === 'fronting' &&
              isAccessAllowed(mainFilterCompanyDocuments?.uuid, 'HR_docs_reports', accessList, defaultPermissions) && (
                <TabPanel value="docs2">
                  <AgentReportsAndActs
                    actsAndReports={actsAndReports}
                    fetchNextPage={fetchNextPageActs}
                    isFetchingNextPageReportFiles={isFetchingNextPageReportFiles}
                    hasNextPagePageReportFiles={hasNextPageReportFiles}
                  />
                </TabPanel>
              )}
          </TabContext>
        </Box>
      ) : (
        <InfoText variant="h5">
          Для просмотра данного раздела у вас недостаточно прав, обратитесь к администратору{' '}
          {companyAdminsData
            ?.slice(0, 3)
            ?.map((item) => item?.user?.email)
            ?.join(',')}
          , если вам необходимы данные для работы
        </InfoText>
      )}
    </>
  );
};
